import {
  emailRegEx,
  phoneRegEx,
  websiteRegex,
  zipCodeRegEx,
} from 'utils/RegularExpressions';
import * as Yup from 'yup';

const step1Schema = Yup.object({
  howInfo: Yup.object({
    name: Yup.string()
      .required('Required')
      .max(250, 'Must be less than 250 characters.'),
    website: Yup.string()
      .max(50, 'Must be less than 50 characters.')
      .matches(websiteRegex, {
        excludeEmptyString: true,
        message: 'Please enter a valid website address',
      }),
    address: Yup.string()
      .max(250, 'Must be less than 250 characters.')
      .required('Required'),
    city: Yup.string()
      .max(250, 'Must be less than 250 characters.')
      .required('Required'),
    state: Yup.string().required('Required'),
    zipCode: Yup.string()
      .required('Required')
      .typeError('Must be a number')
      .matches(zipCodeRegEx, {
        excludeEmptyString: true,
        message: 'Please enter a valid Zip / Postal code',
      }),
    clergyLeader: Yup.string()
      .required('Required')
      .max(250, 'Must be less than 250 characters.'),
    phone: Yup.string().required('Required').matches(phoneRegEx, {
      excludeEmptyString: true,
      message: 'Please enter a valid phone number',
    }),
    email: Yup.string()
      .required('Required')
      .matches(emailRegEx, 'Please enter a valid email address'),
  }),
});

const step2Schema = Yup.object({
  howStructure: Yup.object({
    q1: Yup.string().required('Required'),
    'q1-boardChair': Yup.string().when('q1', {
      is: 'yes',
      then: (schema) =>
        schema
          .required('Required')
          .max(250, 'Must be less than 250 characters'),
      otherwise: (schema) => schema.nullable(),
    }),
    'q1-phone': Yup.string().when('q1', {
      is: 'yes',
      then: (schema) => schema.required('Required'),
      otherwise: (schema) => schema.nullable(),
    }),
    'q1-email': Yup.string().when('q1', {
      is: 'yes',
      then: (schema) =>
        schema
          .required('Required')
          .matches(emailRegEx, 'Please enter a valid email address')
          .max(250, 'Must be less than 250 characters'),
      otherwise: (schema) => schema.nullable(),
    }),
    q2: Yup.string().required('Required'),
    q3: Yup.string().when('q2', {
      is: 'yes',
      then: (schema) => schema.required('Required'),
      otherwise: (schema) => schema.nullable(),
    }),
    'q3-nonProfitName': Yup.string().when('q3', {
      is: 'yes',
      then: (schema) =>
        schema
          .required('Required')
          .max(250, 'Must be less than 250 characters'),
      otherwise: (schema) => schema.nullable(),
    }),
    'q3-nonProfitAddress': Yup.string().when('q3', {
      is: 'yes',
      then: (schema) =>
        schema
          .required('Required')
          .max(500, 'Must be less than 500 characters'),
      otherwise: (schema) => schema.nullable(),
    }),
    'q3-executiveDirector': Yup.string().when('q3', {
      is: 'yes',
      then: (schema) =>
        schema
          .required('Required')
          .max(250, 'Must be less than 250 characters'),
      otherwise: (schema) => schema.nullable(),
    }),
    'q3-phone': Yup.string().when('q3', {
      is: 'yes',
      then: (schema) => schema.required('Required'),
      otherwise: (schema) => schema.nullable(),
    }),
    'q3-email': Yup.string().when('q3', {
      is: 'yes',
      then: (schema) =>
        schema
          .required('Required')
          .matches(emailRegEx, 'Please enter a valid email address')
          .max(250, 'Must be less than 250 characters'),
      otherwise: (schema) => schema.nullable(),
    }),
    'q3-fteCount': Yup.number()
      .transform((v) => (isNaN(v) ? undefined : v))
      .when('q3', {
        is: 'yes',
        then: (schema) =>
          schema
            .required('Required')
            .positive('Must be positive number')
            .integer('Must be integer')
            .lessThan(1000000, 'Must be less than 1,000,000')
            .transform((v) => (isNaN(v) ? undefined : v)),
        otherwise: (schema) => schema.nullable(),
      }),
    'q3-pteCount': Yup.number()
      .transform((v) => (isNaN(v) ? undefined : v))
      .when('q3', {
        is: 'yes',
        then: (schema) =>
          schema
            .positive('Must be positive number')
            .integer('Must be integer')
            .lessThan(1000000, 'Must be less than 1,000,000'),
        otherwise: (schema) => schema.nullable(),
      }),
    'q3-operatingBudget': Yup.string().when('q3', {
      is: 'yes',
      then: (schema) => schema.max(20, 'Must be less than 20 digits'),
      otherwise: (schema) => schema.nullable(),
    }),
    'q3-yearsService': Yup.number()
      .transform((v) => (isNaN(v) ? undefined : v))
      .when('q3', {
        is: 'yes',
        then: (schema) =>
          schema
            .required('Required')
            .positive('Must be positive number')
            .integer('Must be integer')
            .lessThan(1000, 'Must be less than 1000'),
        otherwise: (schema) => schema.nullable(),
      }),
    'q3-meetingDate': Yup.date()
      .transform((v) => (isNaN(v) ? undefined : v))
      .when('q3', {
        is: 'yes',
        then: (schema) =>
          schema.max(new Date(), 'Must be past date.').typeError('Required'),
        otherwise: (schema) => schema.nullable(),
      }),
  }),
});

const step3Schema = Yup.object({
  projectInfo: Yup.object({
    q1: Yup.string().required('Required'),
    'q1-nf1': Yup.string().when('q1', {
      is: 'no',
      then: (schema) =>
        schema
          .required('Required')
          .max(250, 'Must be less than 250 characters'),
      otherwise: (schema) => schema.nullable(),
    }),
    'q1-propertyAddress': Yup.string().when('q1', {
      is: 'yes',
      then: (schema) =>
        schema
          .required('Required')
          .max(250, 'Must be less than 250 characters'),
      otherwise: (schema) => schema.nullable(),
    }),
    'q1-propertyOwner': Yup.string().when('q1', {
      is: 'yes',
      then: (schema) =>
        schema
          .required('Required')
          .max(250, 'Must be less than 250 characters'),
      otherwise: (schema) => schema.nullable(),
    }),
    'q1-f3': Yup.string().when('q1', {
      is: 'yes',
      then: (schema) =>
        schema
          .required('Required')
          .max(250, 'Must be less than 250 characters'),
      otherwise: (schema) => schema.nullable(),
    }),
    'q1-propertyCurrentlyVacant': Yup.string().when('q1', {
      is: 'yes',
      then: (schema) => schema.required('Required'),
      otherwise: (schema) => schema.nullable(),
    }),
    'q1-demolished': Yup.string().when('q1-propertyCurrentlyVacant', {
      is: 'yes',
      then: (schema) => schema.required('Required'),
      otherwise: (schema) => schema.nullable(),
    }),
    'q1-haveLegalCounsel': Yup.string().when('q1', {
      is: 'yes',
      then: (schema) => schema.required('Required'),
      otherwise: (schema) => schema.nullable(),
    }),
    'q1-haveConsultantPartner': Yup.string().when('q1', {
      is: 'yes',
      then: (schema) => schema.required('Required'),
      otherwise: (schema) => schema.nullable(),
    }),
    'q1-haveDevelopmentPartner': Yup.string().when('q1', {
      is: 'yes',
      then: (schema) => schema.required('Required'),
      otherwise: (schema) => schema.nullable(),
    }),
    'q1-haveMarketAnalysis': Yup.string().when('q1', {
      is: 'yes',
      then: (schema) => schema.required('Required'),
      otherwise: (schema) => schema.nullable(),
    }),
    'q1-haveDevelopmentBudgetProforma': Yup.string().when('q1', {
      is: 'yes',
      then: (schema) => schema.required('Required'),
      otherwise: (schema) => schema.nullable(),
    }),
    'q1-haveFeasibilityAnalysis': Yup.string().when('q1', {
      is: 'yes',
      then: (schema) => schema.required('Required'),
      otherwise: (schema) => schema.nullable(),
    }),
    'q1-hadArchitecturalDrawings': Yup.string().when('q1', {
      is: 'yes',
      then: (schema) => schema.required('Required'),
      otherwise: (schema) => schema.nullable(),
    }),
  }),
});

const step4Schema = Yup.object({
  developmentAndCommunityGoals: Yup.object({
    goals: Yup.array(),
    other: Yup.string().nullable(),
  }),
});

const step5Schema = Yup.object({
  fbdiGoals: Yup.object({
    goals: Yup.array(),
    other: Yup.string().nullable(),
  }),
});

export const FBDIValidationSchema = [
  step1Schema,
  step2Schema,
  step3Schema,
  step4Schema,
  step5Schema,
];
